import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import {
  Building2,
  CheckCircle2,
  Verified,
  Users,
  UserCheck,
  Search,
  MapPin,
  Download,
  Upload,
  Plus,
  Eye,
  Edit,
  Award,
  Trash2,
  FileText,
  Loader2,
  LayoutGrid,
  List,
  X,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  RotateCcw,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

const STATUS_COLORS = {
  active: 'badge-green', inactive: 'badge-gray', suspended: 'badge-red', winding_up: 'badge-orange'
};

export default function OrgsList() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [csos, setCsos] = useState([]);
  const [pagination, setPagination] = useState({});
  const [provinces, setProvinces] = useState([]);
  const [thematics, setThematics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const [filters, setFilters] = useState({
    search: '', province_id: '', thematic_area_id: '',
    zan_certified: '', women_led: '', youth_led: '',
    operational_status: '', sort: 'name', page: 1, limit: 25,
  });
  const [activeFilters, setActiveFilters] = useState([]);

  useEffect(() => {
    Promise.all([API.get('/lookup/provinces'), API.get('/lookup/thematic')])
      .then(([p, t]) => { setProvinces(p.data); setThematics(t.data); });
    const f = searchParams.get('filter');
    if (f === 'draft') setFilters(x => ({...x, is_published: 'false'}));
    if (f === 'uncertified') setFilters(x => ({...x, zan_certified: 'false'}));
  }, []);

  const fetchCsos = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ ...filters, admin_view: 'true' });
    API.get(`/csos?${p}`)
      .then(r => { setCsos(r.data.data); setPagination(r.data.pagination); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { fetchCsos(); }, [fetchCsos]);

  // Build active filter chips
  useEffect(() => {
    const chips = [];
    if (filters.search) chips.push({ label: filters.search, key: 'search' });
    if (filters.zan_certified === 'true') chips.push({ label: 'Certified', key: 'zan_certified' });
    if (filters.zan_certified === 'false') chips.push({ label: 'Not Certified', key: 'zan_certified' });
    if (filters.women_led === 'true') chips.push({ label: 'Women Led', key: 'women_led' });
    if (filters.youth_led === 'true') chips.push({ label: 'Youth Led', key: 'youth_led' });
    const prov = provinces.find(p => String(p.id) === String(filters.province_id));
    if (prov) chips.push({ label: `${prov.name} Province`, key: 'province_id' });
    setActiveFilters(chips);
  }, [filters, provinces]);

  const setFilter = (k, v) => setFilters(f => ({...f, page: 1, [k]: v}));
  const removeFilter = (key) => setFilter(key, '');
  const clearAll = () => setFilters({ search:'', province_id:'', thematic_area_id:'', zan_certified:'', women_led:'', youth_led:'', operational_status:'', sort:'name', page:1, limit:25 });

  const handleCertify = async (id, val) => {
    try {
      await API.patch(`/csos/${id}/certify`, { certify: val });
      toast.success(val ? 'CSO certified' : 'Certification removed');
      fetchCsos();
    } catch { toast.error('Failed'); }
  };

  const handlePublish = async (id) => {
    try {
      const r = await API.patch(`/csos/${id}/publish`);
      toast.success(r.data.is_published ? 'Published' : 'Unpublished');
      fetchCsos();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await API.delete(`/csos/${id}`);
      toast.success('Deleted');
      fetchCsos();
    } catch { toast.error('Failed'); }
  };

  const handleBulkPublish = async (publish) => {
    if (!selected.length) return;
    try {
      await API.post('/csos/bulk-publish', { ids: selected, publish });
      toast.success(`${selected.length} organisations ${publish ? 'published' : 'unpublished'}`);
      setSelected([]); fetchCsos();
    } catch { toast.error('Failed'); }
  };

  const handleExport = () => { window.open('/api/csos/export/csv', '_blank'); };

  // Download Excel template
  const handleDownloadTemplate = () => {
    window.open('/api/import/template', '_blank');
  };

  // Import Excel file
  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await API.post('/import/excel', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { created, updated, errors } = r.data;
      let msg = `Import complete: ${created || 0} created, ${updated || 0} updated`;
      if (errors && errors.length > 0) {
        msg += `. ${errors.length} rows had errors.`;
        console.warn('Import errors:', errors);
      }
      toast.success(msg, { autoClose: 5000 });
      fetchCsos();
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Import failed';
      toast.error(errMsg);
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);
  const toggleAll = () => setSelected(s => s.length === csos.length ? [] : csos.map(c=>c.id));

  const canManage = ['super_admin','admin','data_entry'].includes(user?.role);
  const canDelete = user?.role === 'super_admin';
  const canCertify = ['super_admin','admin','field_officer'].includes(user?.role);

  return (
    <div className="anim-fadeIn">
      {/* Stat row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:20 }}>
        {[
          { label:'Total Organisations', value:pagination.total||0, icon: Building2, color:'#e8f5e8', iconColor:'#2e7d32' },
          { label:'Published',            value:csos.filter(c=>c.is_published).length,  icon: CheckCircle2, color:'#dbeafe', iconColor:'#1e40af' },
          { label:'Verified/Certified',   value:csos.filter(c=>c.zan_certified).length, icon: Verified, color:'#e0e7ff', iconColor:'#3730a3' },
          { label:'Women Led',            value:csos.filter(c=>c.women_led).length,     icon: UserCheck, color:'#fce7f3', iconColor:'#9d174d' },
          { label:'Youth Led',            value:csos.filter(c=>c.youth_led).length,     icon: Users, color:'#fef3c7', iconColor:'#92400e' },
        ].map((s,i) => {
          const StatIcon = s.icon;
          return (
            <div key={i} className="card" style={{ padding:'14px 16px', display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:36, height:36, borderRadius:8, background:s.color, display:'flex', alignItems:'center', justifyContent:'center', color:s.iconColor }}>
                <StatIcon size={18} />
              </div>
              <div>
                <div style={{ fontSize:'1.2rem', fontWeight:800, lineHeight:1 }}>{s.value.toLocaleString()}</div>
                <div style={{ fontSize:'0.7rem', color:'var(--gray-500)', marginTop:2 }}>{s.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters card */}
      <div className="filter-bar" style={{ marginBottom:16 }}>
        <div className="filter-row">
          <div style={{ flex:2, minWidth:200 }}>
            <div className="input-group">
              <span className="input-icon"><Search size={16} /></span>
              <input className="form-control" placeholder="Search by name, reg no, acronym, email..."
                value={filters.search} onChange={e => setFilter('search', e.target.value)}
                onKeyDown={e => e.key==='Enter' && fetchCsos()}/>
            </div>
          </div>
          <div style={{ flex:1, minWidth:140 }}>
            <select className="form-control" value={filters.province_id} onChange={e => setFilter('province_id', e.target.value)}>
              <option value="">All Provinces</option>
              {provinces.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div style={{ flex:1, minWidth:140 }}>
            <select className="form-control" value={filters.thematic_area_id} onChange={e => setFilter('thematic_area_id', e.target.value)}>
              <option value="">All Thematic Areas</option>
              {thematics.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div style={{ flex:1, minWidth:130 }}>
            <select className="form-control" value={filters.zan_certified} onChange={e => setFilter('zan_certified', e.target.value)}>
              <option value="">Certification Status</option>
              <option value="true">Certified</option>
              <option value="false">Not Certified</option>
            </select>
          </div>
          <div style={{ flex:1, minWidth:130 }}>
            <select className="form-control" value={filters.operational_status} onChange={e => setFilter('operational_status', e.target.value)}>
              <option value="">Operational Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          <div style={{ flex:1, minWidth:110 }}>
            <select className="form-control" value={filters.women_led} onChange={e => setFilter('women_led', e.target.value)}>
              <option value="">Women Led</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          <div style={{ flex:1, minWidth:110 }}>
            <select className="form-control" value={filters.youth_led} onChange={e => setFilter('youth_led', e.target.value)}>
              <option value="">Youth Led</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        </div>

        {activeFilters.length > 0 && (
          <div className="active-filters">
            <span style={{ fontSize:'0.75rem', fontWeight:600, color:'var(--gray-500)' }}>Active Filters:</span>
            {activeFilters.map((f,i) => (
              <span key={i} className="filter-chip">
                {f.label} <button onClick={() => removeFilter(f.key)}><X size={12} /></button>
              </span>
            ))}
            <button onClick={clearAll} style={{ background:'none', border:'none', color:'var(--crimson)', fontSize:'0.75rem', fontWeight:600, cursor:'pointer' }}>Clear all</button>
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14, flexWrap:'wrap', gap:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <h3 style={{ fontSize:'0.95rem', fontWeight:700 }}>
            Organisations <span style={{ color:'var(--gray-400)', fontWeight:400 }}>({pagination.total||0} results)</span>
          </h3>
          {selected.length > 0 && (
            <div style={{ display:'flex', gap:6, marginLeft:12 }}>
              <span style={{ fontSize:'0.78rem', color:'var(--gray-500)' }}>{selected.length} selected</span>
              <button onClick={() => handleBulkPublish(true)} className="btn btn-sm btn-secondary">Publish All</button>
              <button onClick={() => handleBulkPublish(false)} className="btn btn-sm btn-secondary">Unpublish All</button>
            </div>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ display:'flex', border:'1px solid var(--gray-200)', borderRadius:6, overflow:'hidden' }}>
            {[[List,'table'],[LayoutGrid,'grid']].map(([IconComponent,mode]) => (
              <button key={mode} onClick={() => setViewMode(mode)}
                style={{ padding:'6px 10px', border:'none', background:viewMode===mode?'var(--green)':'white', color:viewMode===mode?'white':'var(--gray-500)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <IconComponent size={16} />
              </button>
            ))}
          </div>

          <select className="form-control" style={{ width:140 }} value={filters.sort} onChange={e => setFilter('sort', e.target.value)}>
            <option value="name">Name A–Z</option>
            <option value="newest">Newest First</option>
            <option value="views">Most Viewed</option>
          </select>

          <select className="form-control" style={{ width:100 }} value={filters.limit} onChange={e => setFilter('limit', e.target.value)}>
            {[10,25,50,100].map(n => <option key={n} value={n}>{n} per page</option>)}
          </select>

          <button onClick={handleExport} className="btn btn-sm btn-secondary" style={{ display:'flex', alignItems:'center', gap:4 }}>
            <Download size={14} /> Export CSV
          </button>

          {/* Import & Template */}
          {canManage && (
            <>
              <button
                onClick={handleDownloadTemplate}
                className="btn btn-sm btn-secondary"
                style={{ display:'flex', alignItems:'center', gap:4 }}
                title="Download the Excel template to fill in your data"
              >
                <FileSpreadsheet size={14} /> Template
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display:'none' }}
                onChange={handleImport}
              />
              <button
                onClick={() => fileRef.current.click()}
                disabled={importing}
                className="btn btn-sm btn-secondary"
                style={{ display:'flex', alignItems:'center', gap:4 }}
              >
                {importing ? (
                  <><Loader2 size={14} className="spin" /> Importing...</>
                ) : (
                  <><Upload size={14} /> Import Excel</>
                )}
              </button>
            </>
          )}

          {canManage && (
            <Link to="/admin/organisations/new" className="btn btn-primary btn-sm" style={{ display:'flex', alignItems:'center', gap:4 }}>
              <Plus size={14} /> Add Organisation
            </Link>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading-center"><Loader2 size={32} className="spin" /></div>
      ) : viewMode === 'table' ? (
        <div className="card">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width:36 }}>
                    <input type="checkbox" checked={selected.length===csos.length&&csos.length>0} onChange={toggleAll}/>
                  </th>
                  <th>Organisation</th>
                  <th>Registration No.</th>
                  <th>Province / District</th>
                  <th>Thematic Areas</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {csos.length === 0 ? (
                  <tr><td colSpan={7}><div className="empty-state"><div className="empty-state-icon"><Search size={48} /></div><h3>No organisations found</h3><p>Try adjusting your filters</p></div></td></tr>
                ) : csos.map(cso => (
                  <tr key={cso.id}>
                    <td><input type="checkbox" checked={selected.includes(cso.id)} onChange={() => toggleSelect(cso.id)}/></td>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:34, height:34, borderRadius:6, background:'var(--green-50)', border:'1px solid var(--green-100)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:'var(--green)', fontSize:'0.7rem', flexShrink:0, overflow:'hidden' }}>
                          {cso.logo_url ? <img src={`${cso.logo_url}`} alt="" style={{ width:'100%', height:'100%', objectFit:'contain' }}/> : (cso.acronym||cso.org_name).slice(0,2).toUpperCase()}
                        </div>
                        <div>
                          <Link to={`/admin/organisations/${cso.id}`} style={{ fontWeight:600, fontSize:'0.85rem', color:'var(--gray-800)' }}>{cso.org_name}</Link>
                          {cso.acronym && <div style={{ fontSize:'0.72rem', color:'var(--gray-400)' }}>{cso.acronym}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize:'0.82rem', color:'var(--gray-600)' }}>{cso.reg_number||'—'}</td>
                    <td>
                      {cso.province && <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:'0.8rem' }}>
                        <MapPin size={12} /><span>{cso.province}{cso.district?` / ${cso.district}`:''}</span>
                      </div>}
                    </td>
                    <td>
                      <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                        {cso.thematic_areas?.slice(0,2).map((t,i) => <span key={i} className="badge badge-green" style={{ fontSize:'0.68rem' }}>{t}</span>)}
                        {cso.thematic_areas?.length>2 && <span className="badge badge-gray" style={{ fontSize:'0.68rem' }}>+{cso.thematic_areas.length-2}</span>}
                      </div>
                    </td>
                    <td>
                      <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                        <span className={`badge ${cso.is_published?'badge-green':'badge-yellow'}`} style={{ width:'fit-content', fontSize:'0.7rem', display:'flex', alignItems:'center', gap:3 }}>
                          {cso.is_published ? <><CheckCircle2 size={10} /> Published</> : <><FileText size={10} /> Draft</>}
                        </span>
                        <span className={`badge ${cso.zan_certified?'badge-blue':'badge-gray'}`} style={{ width:'fit-content', fontSize:'0.7rem', display:'flex', alignItems:'center', gap:3 }}>
                          {cso.zan_certified ? <><Verified size={10} /> Certified</> : 'Not Certified'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display:'flex', gap:4 }}>
                        <Link to={`/admin/organisations/${cso.id}`} className="btn btn-sm btn-secondary" title="View" style={{ padding:'4px 6px' }}><Eye size={14} /></Link>
                        {canManage && <Link to={`/admin/organisations/edit/${cso.id}`} className="btn btn-sm btn-secondary" title="Edit" style={{ padding:'4px 6px' }}><Edit size={14} /></Link>}
                        {canCertify && (
                          <button onClick={() => handleCertify(cso.id, !cso.zan_certified)} className="btn btn-sm btn-secondary" title={cso.zan_certified?'Remove cert':'Certify'} style={{ padding:'4px 6px' }}>
                            {cso.zan_certified ? <RotateCcw size={14} /> : <Award size={14} />}
                          </button>
                        )}
                        {['super_admin','admin'].includes(user?.role) && (
                          <button onClick={() => handlePublish(cso.id)} className="btn btn-sm btn-secondary" title={cso.is_published?'Unpublish':'Publish'} style={{ padding:'4px 6px' }}>
                            {cso.is_published ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => handleDelete(cso.id, cso.org_name)} className="btn btn-sm btn-danger" title="Delete" style={{ padding:'4px 6px' }}><Trash2 size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid-3">
          {csos.map(cso => (
            <div key={cso.id} className="card card-hover" style={{ overflow:'hidden' }}>
              <div style={{ padding:'16px 16px 12px', borderBottom:'1px solid var(--gray-100)', display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:44, height:44, borderRadius:8, background:'var(--green-50)', border:'1px solid var(--green-100)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:'var(--green)', fontSize:'0.85rem', flexShrink:0 }}>
                  {(cso.acronym||cso.org_name).slice(0,2).toUpperCase()}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:'0.85rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cso.org_name}</div>
                  <div style={{ fontSize:'0.72rem', color:'var(--gray-400)' }}>{cso.province||'Zimbabwe'}</div>
                </div>
                {cso.zan_certified && <span className="zan-badge"><CheckCircle2 size={14} /></span>}
              </div>
              <div style={{ padding:'10px 16px', fontSize:'0.78rem', color:'var(--gray-500)' }}>
                {cso.reg_number && <div style={{ display:'flex', alignItems:'center', gap:4 }}><FileText size={12} /> {cso.reg_number}</div>}
                <div style={{ display:'flex', gap:4, marginTop:6, flexWrap:'wrap' }}>
                  <span className={`badge ${cso.is_published?'badge-green':'badge-yellow'}`} style={{ fontSize:'0.65rem' }}>{cso.is_published?'Published':'Draft'}</span>
                  {cso.thematic_areas?.slice(0,2).map((t,i) => <span key={i} className="badge badge-blue" style={{ fontSize:'0.65rem' }}>{t}</span>)}
                </div>
              </div>
              <div style={{ padding:'10px 16px', borderTop:'1px solid var(--gray-100)', display:'flex', gap:6 }}>
                <Link to={`/admin/organisations/${cso.id}`} className="btn btn-sm btn-secondary" style={{ flex:1, justifyContent:'center' }}>View</Link>
                {canManage && <Link to={`/admin/organisations/edit/${cso.id}`} className="btn btn-sm btn-primary" style={{ flex:1, justifyContent:'center' }}>Edit</Link>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:20 }}>
          <span style={{ fontSize:'0.78rem', color:'var(--gray-500)' }}>
            Showing {((filters.page-1)*filters.limit)+1}–{Math.min(filters.page*filters.limit, pagination.total)} of {pagination.total}
          </span>
          <div className="pagination">
            <button className="pg-btn" onClick={() => setFilter('page', 1)} disabled={filters.page<=1}><ChevronsLeft size={14} /></button>
            <button className="pg-btn" onClick={() => setFilter('page', filters.page-1)} disabled={filters.page<=1}><ChevronLeft size={14} /></button>
            {Array.from({length:Math.min(pagination.pages,7)}, (_,i) => {
              let p; const total = pagination.pages, cur = filters.page;
              if (total<=7) p=i+1;
              else if (cur<=4) p=i+1;
              else if (cur>=total-3) p=total-6+i;
              else p=cur-3+i;
              return <button key={p} className={`pg-btn${filters.page===p?' active':''}`} onClick={()=>setFilter('page',p)}>{p}</button>;
            })}
            <button className="pg-btn" onClick={() => setFilter('page', filters.page+1)} disabled={filters.page>=pagination.pages}><ChevronRight size={14} /></button>
            <button className="pg-btn" onClick={() => setFilter('page', pagination.pages)} disabled={filters.page>=pagination.pages}><ChevronsRight size={14} /></button>
          </div>
        </div>
      )}
    </div>
  );
}